import { FC, useEffect } from 'react';
import {
  Control,
  Controller,
  UseFormSetValue,
  useWatch,
  UseFormTrigger,
} from 'react-hook-form';
import { Box, Grid, Typography } from '@mui/material';
import type { Account } from '../../../../../../../../types/api';
import { Asset } from '../../../../../modules/loan/asset';
import { PLACEHOLDERS, UNITED_STATES } from '../../../../../constants';
import { InitialValues, timelineEstimations } from '../schema';
import { CustomTooltip } from '../../../../CustomTooltip';
import InputField from '../../../../hook_form_fields/InputField';
import NumberField from '../../../../hook_form_fields/NumberField';
import SelectField from '../../../../hook_form_fields/SelectField';
import SelectableCardGroup from '../../../../SelectableCardGroup';
import { InfoIcon } from '../../../../loan_application/icons';
import NewBorrowerFormFieldset from '../NewBorrowerFormFieldset';
import {
  getAssetOptionsByContractorType,
  hasFlex,
} from '../../../../../modules/installer/account';

const setValueOptions = {
  shouldValidate: true,
  shouldDirty: true,
  shouldTouch: true,
};

const ProjectAssets: FC<{
  control: Control<InitialValues>;
  setValue: UseFormSetValue<InitialValues>;
  account: Account | null | undefined;
  trigger: UseFormTrigger<InitialValues>;
}> = ({ control, setValue, account, trigger }) => {
  const projectAssets = useWatch({ control, name: 'projectAssets' });

  // Clear the Other description/cost when Other gets deselected.
  useEffect(() => {
    if (!projectAssets?.other) {
      if (projectAssets?.otherValue) {
        setValue('projectAssets.otherValue', '');
      }
      if (projectAssets?.otherPrice != null) {
        setValue('projectAssets.otherPrice', undefined);
      }
    }
  }, [projectAssets?.other]);

  const filteredCardOptions = getAssetOptionsByContractorType(account).map(
    (opt) => ({
      key: opt.key,
      label: opt.assetType,
    })
  );
  const showFlexLayoutFields = hasFlex(account);

  useEffect(() => {
    if (projectAssets?.other) {
      trigger('projectAssets.otherValue');
    }
  }, [projectAssets?.other, trigger]);

  return (
    <NewBorrowerFormFieldset title="Project Information">
      <Grid container maxWidth="md" spacing={2}>
        <Grid item xs={12}>
          <Typography variant="body2" mb={1}>
            Which of the following improvements will be funded by the loan?
            <CustomTooltip title="Underwriting may require additional information about these improvements">
              <InfoIcon
                sx={{
                  fontSize: '.75rem',
                  marginTop: '.3125rem',
                  marginLeft: '.25rem',
                  verticalAlign: 'center',
                }}
              />
            </CustomTooltip>
            <Typography variant="body2" color="error" component="span">
              {' '}
              *
            </Typography>
          </Typography>

          <Controller
            control={control}
            name="projectAssetsChecked"
            render={({ fieldState, formState }) => {
              const { error } = fieldState;
              const touched = formState.touchedFields['projectAssets'];
              const anyTouched =
                touched && Asset.OPTIONS.some((opt) => touched[opt.key]);
              const shouldShow =
                !!error && (anyTouched || formState.isSubmitted);
              return shouldShow ? (
                <Typography
                  variant="body2"
                  color="error"
                  component="div"
                  mb={1}
                >
                  {error.message}
                </Typography>
              ) : (
                <></>
              );
            }}
          />

          <SelectableCardGroup
            columns={3}
            options={filteredCardOptions}
            isSelected={(key) => !!projectAssets?.[key]}
            onToggle={(key) =>
              setValue(
                `projectAssets.${key}` as const,
                !projectAssets?.[key],
                setValueOptions
              )
            }
            renderExpanded={(opt) =>
              opt.key === 'other' ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <InputField
                    control={control}
                    name="projectAssets.otherValue"
                    placeholder="Describe the improvement"
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={4}
                    maxLength={140}
                    touched
                  />
                  <NumberField
                    control={control}
                    name="projectAssets.otherPrice"
                    thousandSeparator
                    startAdornmentText="$"
                    placeholder={PLACEHOLDERS.OPTIONAL}
                    fullWidth
                    allowNegative={false}
                    decimalScale={2}
                  />
                </Box>
              ) : null
            }
          />
        </Grid>

        {showFlexLayoutFields && (
          <>
            <Grid item xs={12} md={6} mt={2}>
              <SelectField
                fullWidth
                control={control}
                name="application.sys_prop_address_state"
                label="Property's State where the system will be installed"
                required
                choices={UNITED_STATES}
              />
            </Grid>
            <Grid item xs={12} md={6} mt={{ xs: 0, md: 2 }}>
              <SelectField
                fullWidth
                control={control}
                name="application.project_timeline_estimation"
                label="Project Timeline Estimation"
                required
                choices={timelineEstimations}
                tooltip="Please indicate your best estimate at this time, it is ok if this information changes later."
              />
            </Grid>
          </>
        )}
      </Grid>
    </NewBorrowerFormFieldset>
  );
};

export default ProjectAssets;
